/**
 * The mapping layer between what the backend says and what the scene shows.
 *
 * The domain has `BugState`: Waiting, Assigned, InProgress, Testing, Blocked, ReadyForReview,
 * Completed, Reopened. Those are engineering facts and they belong to the server.
 *
 * The scene has `VisualBugState`: contained, walking, worked-on, tested, awaiting review, defeated.
 * Those are presentation, and they exist only in the browser.
 *
 * Keeping them apart is the rule that makes the whole feature safe. The animation reads domain
 * state and never writes it; a bug is not fixed because a swatter animation finished, it is fixed
 * because the server said so and the animation is what that looked like. Every translation between
 * the two vocabularies happens here, so there is one place to check when the two disagree.
 */

import type { BugState, Priority } from '../lib/types'

/** What the scene is doing with a bug. Never persisted, never sent anywhere. */
export type VisualBugState =
  /** Inside the containment unit, drifting. */
  | 'contained'
  /** Crossing the floor towards the developer it was assigned to. */
  | 'walking'
  /** At the workstation, being worked on. */
  | 'workedOn'
  /** Held under a scanner while tests run. */
  | 'tested'
  /** On a tester's bench, under their magnifying glass. */
  | 'underTest'
  /** Frozen behind a barrier: work is stopped, which is not the same as nobody working. */
  | 'blocked'
  /** Stunned by the racket and caught under a glass, waiting for a person to say it is fixed. */
  | 'awaitingReview'
  /** Launched off the floor. Removed once the animation ends. */
  | 'defeated'
  /** Returning from off-screen after a reopen. */
  | 'returning'

export const VISUAL_FOR_STATE: Record<BugState, VisualBugState> = {
  Registered: 'contained',
  Waiting: 'contained',
  Assigned: 'walking',
  InProgress: 'workedOn',
  Testing: 'tested',
  InTesting: 'underTest',
  Blocked: 'blocked',
  ReadyForReview: 'awaitingReview',
  Completed: 'defeated',
  Reopened: 'returning',
  Cancelled: 'defeated',
}

/**
 * Where a bug is drawn, given what it is doing.
 *
 * A bug with no developer has nowhere to go but containment, whatever its status claims — an
 * Assigned bug whose developer is not on screen would otherwise walk to the origin.
 */
export function visualStateFor(state: BugState, hasDeveloper: boolean): VisualBugState {
  const visual = VISUAL_FOR_STATE[state] ?? 'contained'

  if (!hasDeveloper && visual !== 'defeated' && visual !== 'returning') return 'contained'

  return visual
}

/** True while the bug belongs to the containment unit rather than to the floor. */
export function isContained(visual: VisualBugState): boolean {
  return visual === 'contained'
}

/** True while the bug should be drawn at a workstation. */
export function isAtStation(visual: VisualBugState): boolean {
  return visual === 'workedOn' || visual === 'tested' || visual === 'blocked' || visual === 'awaitingReview'
    || visual === 'underTest'
}

/**
 * How urgent a bug is, as a number, so lists and stacking order agree.
 *
 * Priority alone is not enough: a blocked critical and a blocked trivial need different attention,
 * and so do a critical waiting and a critical already in review.
 */
export function urgency(state: BugState, priority: Priority): number {
  const byState: Record<string, number> = {
    Blocked: 0,
    Testing: 1,
    InTesting: 1,
    InProgress: 2,
    ReadyForReview: 3,
    Assigned: 4,
    Reopened: 4,
  }

  const byPriority: Record<Priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 }

  return (byState[state] ?? 9) * 10 + byPriority[priority]
}

/**
 * The accent for a priority.
 *
 * Colour is the fastest signal and the least reliable one — it fails for a colour-blind reader and
 * in a screenshot pasted into a document. Everything that uses these also carries the priority in
 * words, and the creature carries it in shape as well.
 */
export const PRIORITY_ACCENT: Record<Priority, string> = {
  Critical: 'var(--danger)',
  High: 'var(--warn)',
  Medium: 'var(--accent)',
  Low: 'var(--text-muted)',
}

/** How many spots the creature wears. A second, non-colour channel for severity. */
export const PRIORITY_MARKS: Record<Priority, number> = {
  Critical: 3,
  High: 2,
  Medium: 1,
  Low: 0,
}
